import { Clip } from '../types/clip-types';
import { SegmentsTrack } from '../types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { SegmentType } from '../types/Segment/Type';
import { TrackType } from '../types/Track/Type';
import { getAllCompositeSegments, getAllSegments, getClipFPS } from '../utils/clipUtils';

export const injectSegmentMetaDataInClip = (clip: Clip): Clip => {
  const allCompositeSegments = getAllCompositeSegments(clip);
  const hasMultipleCompositeSegments = allCompositeSegments.length > 1;
  if (hasMultipleCompositeSegments) {
    throw new Error('Multiple composite segments found. This should not happen.');
  }
  const compositeSegment = allCompositeSegments[0];
  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found. This should not happen.');
  }
  clip.asset_metadata = {
    ...clip.asset_metadata,
    deletes: compositeSegment.segment_metadata.deletes,
    edits: compositeSegment.segment_metadata.edits,
    merges: compositeSegment.segment_metadata.merges,
    excludes: compositeSegment.segment_metadata.excludes,
    captions: compositeSegment.segment_metadata.captions,
    fit: compositeSegment.segment_metadata.fitMode,
    scenes: compositeSegment.segment_metadata.scenes,
    duration: compositeSegment.segment_metadata.duration,
    maxVideoDuration: compositeSegment.segment_metadata.maxVideoDuration,
    start: compositeSegment.segment_metadata.start,
    end: compositeSegment.segment_metadata.end
  };
  return clip;
};
export const injectSegmentMetaDataInCompositeSegment = (clip: Clip) => {
  const newClip = structuredClone(clip);
  const playerConfig = newClip.asset_metadata.player_config;
  const allCompositeSegments = getAllCompositeSegments(newClip);

  const hasMoreThanOneCompositeSegment = allCompositeSegments.length > 1;

  const allCompositeSegmentHaveMetaData = allCompositeSegments.every(segment => segment.segment_metadata);

  if (!playerConfig) {
    return newClip;
  }

  if (hasMoreThanOneCompositeSegment && !allCompositeSegmentHaveMetaData) {
    throw new Error('Composite segments found but no metadata found. This should not happen.');
  }

  if (allCompositeSegmentHaveMetaData) {
    return newClip;
  }

  const allSegments = getAllSegments(newClip) || [];

  allSegments.forEach(segment => {
    if (segment.type === SegmentType.COMPOSITE) {
      segment.segment_metadata = {
        start: newClip.asset_metadata.start,
        end: newClip.asset_metadata.end,
        deletes: newClip.asset_metadata.deletes || {},
        edits: newClip.asset_metadata.edits || {},
        merges: newClip.asset_metadata.merges || [],
        excludes: newClip.asset_metadata.excludes || [],
        maxVideoDuration: clip.asset_metadata.maxVideoDuration || 0,
        captions_lang: clip.asset_metadata.original_lang,
        captions: newClip.asset_metadata.captions || [],
        fitMode: newClip.asset_metadata.fit,
        scenes: newClip.asset_metadata.scenes || [],
        duration: newClip.asset_metadata.duration || 0
      };
    } else if (segment.type === SegmentType.VIDEO) {
      const maxBound = {
        fromFrame: segment.fromFrame,
        toFrame: segment.toFrame
      };
      const currentBound = {
        fromFrame: segment.fromFrame,
        toFrame: segment.toFrame
      };
      const fps = getClipFPS(newClip);
      segment.maxBounds = maxBound;
      segment.currentBounds = currentBound;
      segment.segment_metadata = {
        start: 0,
        end: (segment.toFrame - segment.fromFrame) / fps,
        deletes: {},
        maxVideoDuration: (segment.toFrame - segment.fromFrame) / fps,
        src: segment.video.details.src || ''
      };
    }
  });

  return newClip;
};

export const injectUpdatedMainTrackInPlayerConfig = (clip: Clip, mainTrack: SegmentsTrack): Clip => {
  if (!clip?.asset_metadata?.player_config || !clip.asset_metadata.player_config.playerConfig) {
    return clip;
  }

  // Replace the main (SEGMENTS) track in player config with the new mainTrack
  const updatedTracks = (clip.asset_metadata.player_config.playerConfig.tracks || []).map(track => {
    if (track.type === TrackType.SEGMENTS) {
      return mainTrack;
    }
    return track;
  });

  // If no main track was present, just append this main track
  const hasMainTrack = updatedTracks.some(track => track.type === TrackType.SEGMENTS);
  const finalTracks = hasMainTrack ? updatedTracks : [mainTrack, ...updatedTracks];

  clip.asset_metadata.player_config.playerConfig.tracks = finalTracks;
  return clip;
};
